/**
 * Admin-only: what the platform's Anthropic calls cost.
 *
 * The backend stores tokens (NORTHSTAR_LLM_CALLS, one row per call) and prices
 * them in code from the model ID — cache reads at ~0.1× the input rate, writes
 * at ~1.25× — so the figure here tracks how Anthropic actually bills, not a
 * flat tokens × rate. The API returns the finest grain (kind × model × topic ×
 * respondent); the rollups below are client-side.
 */
import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLlmCosts } from "@/lib/useAdmin";
import type { LlmCostRow } from "@/lib/types";

const KIND_LABELS: Record<string, string> = {
  interview_turn: "interview turns",
  interview_analysis: "interview reviews",
  interview_summary: "interview summaries",
  synthesis: "synthesis passes",
};

function usd(value: number | null | undefined): string {
  if (value == null) return "—";
  return value >= 100 ? `$${value.toFixed(0)}` : `$${value.toFixed(value >= 1 ? 2 : 4)}`;
}

function tokens(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return String(value);
}

interface Rollup {
  label: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  cacheTokens: number;
  cost: number;
  unpriced: boolean;
}

/** Sum rows into named buckets; a single unpriced row taints its bucket's cost
 *  ("at least this much") rather than being silently counted as $0. */
function rollup(rows: LlmCostRow[], label: (row: LlmCostRow) => string): Rollup[] {
  const buckets = new Map<string, Rollup>();
  for (const row of rows) {
    const key = label(row);
    const bucket = buckets.get(key) ?? {
      label: key,
      calls: 0,
      inputTokens: 0,
      outputTokens: 0,
      cacheTokens: 0,
      cost: 0,
      unpriced: false,
    };
    bucket.calls += row.calls;
    bucket.inputTokens += row.input_tokens;
    bucket.outputTokens += row.output_tokens;
    bucket.cacheTokens += row.cache_creation_tokens + row.cache_read_tokens;
    if (row.cost_usd == null) bucket.unpriced = true;
    else bucket.cost += row.cost_usd;
    buckets.set(key, bucket);
  }
  return [...buckets.values()].sort((a, b) => b.cost - a.cost);
}

function RollupTable({ title, rows }: { title: string; rows: Rollup[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      <span className="font-sans text-[11px] font-semibold uppercase tracking-wide text-bw-ink-muted">
        {title}
      </span>
      {rows.map((row) => (
        <div key={row.label} className="flex items-baseline justify-between gap-3 font-sans text-xs">
          <span className="truncate text-bw-ink">{row.label}</span>
          <span className="shrink-0 text-bw-ink-muted">
            {row.calls} call{row.calls === 1 ? "" : "s"} · {tokens(row.inputTokens)} in /{" "}
            {tokens(row.outputTokens)} out
            {row.cacheTokens > 0 && <> · {tokens(row.cacheTokens)} cached</>} ·{" "}
            <span className="font-semibold text-bw-navy">
              {row.unpriced ? `≥ ${usd(row.cost)}` : usd(row.cost)}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

export function AdminCosts() {
  const { data, isLoading, isError } = useLlmCosts(true);

  const byKind = useMemo(
    () => rollup(data?.rows ?? [], (row) => KIND_LABELS[row.kind] ?? row.kind),
    [data],
  );
  const byTopic = useMemo(
    () => rollup(data?.rows ?? [], (row) => row.topic_title ?? row.topic_key ?? "(no topic)"),
    [data],
  );
  // Synthesis rows carry no respondent — they aggregate everyone, so they don't
  // belong in a per-person view.
  const byPerson = useMemo(
    () =>
      rollup(
        (data?.rows ?? []).filter((row) => row.respondent),
        (row) => row.respondent_upn ?? row.respondent ?? "",
      ),
    [data],
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">LLM spend</CardTitle>
        <p className="font-sans text-xs text-bw-ink-muted">
          Every Anthropic call the platform makes, priced from its token counts at current list
          rates (cache reads at 0.1× input, cache writes at 1.25×). Interview turns run on Sonnet,
          synthesis passes on Opus.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 pt-0">
        {isLoading && <p className="font-sans text-sm text-bw-ink-muted">Loading spend…</p>}
        {isError && (
          <p className="font-sans text-sm text-bw-ink-muted">Couldn't load LLM costs. Try again shortly.</p>
        )}
        {data && data.rows.length === 0 && (
          <p className="font-sans text-sm text-bw-ink-muted">
            No calls ledgered yet — rows appear as interviews and synthesis passes run.
          </p>
        )}
        {data && data.rows.length > 0 && (
          <>
            <div className="flex items-baseline gap-2">
              <span className="font-sans text-2xl font-bold text-bw-navy">
                {usd(data.total_cost_usd)}
              </span>
              <span className="font-sans text-xs text-bw-ink-muted">
                total to date
                {data.unpriced_calls > 0 && (
                  <> · {data.unpriced_calls} call{data.unpriced_calls === 1 ? "" : "s"} on models the
                  price table doesn't know — not counted</>
                )}
              </span>
            </div>
            <RollupTable title="By kind" rows={byKind} />
            <RollupTable title="By topic" rows={byTopic} />
            <RollupTable title="By person (interviews)" rows={byPerson} />
          </>
        )}
      </CardContent>
    </Card>
  );
}
